import { pointsAreCoLocated } from "./dashboardTraceUtils";

export const STOPPED_TONE_MS = 2.5 * 60 * 1000;
export const MOVING_SPEED_KMH = 1;
export const TELEMETRY_RELEASE_GREEN_MS = 10 * 1000;
export const TELEMETRY_RELEASE_YELLOW_MS = 20 * 1000;

export const MAP_ICON_TONE = {
  moving: "online",
  pausedAssumed: "paused",
  stoppedEmpty: "offline",
  intervalPause: "interval",
};

export function parseSpeedKmh(speed) {
  if (speed == null || speed === "") return null;
  if (typeof speed === "number") {
    return Number.isFinite(speed) ? speed : null;
  }
  const match = String(speed)
    .replace(",", ".")
    .match(/-?[\d.]+/);
  if (!match) return null;
  const value = Number(match[0]);
  return Number.isFinite(value) ? value : null;
}

export function isMovingSpeed(speed) {
  const kmh = parseSpeedKmh(speed);
  return kmh != null && kmh >= MOVING_SPEED_KMH;
}

export function formatMapSpeedLabel(speed) {
  const kmh = parseSpeedKmh(speed);
  if (kmh == null || kmh < MOVING_SPEED_KMH) return null;
  const rounded = kmh >= 10 ? Math.round(kmh) : Number(kmh.toFixed(1));
  return `${rounded} km/h`;
}

export function formatOperatorMapLabel(name, speed) {
  const person = String(name || "").trim();
  const speedLabel = formatMapSpeedLabel(speed);
  if (person && speedLabel) return `${person}  ${speedLabel}`;
  return speedLabel || person;
}

export function parseTimeMs(value) {
  if (value == null || value === "") return null;
  if (typeof value === "number" && Number.isFinite(value)) return value;
  const raw = String(value).trim();
  const br = raw.match(
    /^(\d{1,2})\/(\d{1,2})\/(\d{4})(?:[ T](\d{1,2}):(\d{2})(?::(\d{2}))?)?$/
  );
  if (br) {
    const pad = (part) => String(part || 0).padStart(2, "0");
    const iso = `${br[3]}-${pad(br[2])}-${pad(br[1])}T${pad(br[4])}:${pad(
      br[5]
    )}:${pad(br[6])}-03:00`;
    const parsed = new Date(iso).getTime();
    return Number.isNaN(parsed) ? null : parsed;
  }
  const parsed = new Date(raw).getTime();
  return Number.isNaN(parsed) ? null : parsed;
}

export function isFreshMapReport(timestamp, now = new Date()) {
  const reportMs = parseTimeMs(timestamp);
  if (reportMs == null) return false;
  return now.getTime() - reportMs < STOPPED_TONE_MS;
}

export function nextLastMovedAt({
  previousMovedAt,
  previousPosition,
  nextPosition,
  fallbackAt,
} = {}) {
  if (
    previousPosition &&
    nextPosition &&
    !pointsAreCoLocated(previousPosition, nextPosition)
  ) {
    return fallbackAt || new Date().toISOString();
  }
  return previousMovedAt || fallbackAt || null;
}

export function resolveTelemetryPauseTone(pausedAt, now = new Date()) {
  const pausedMs = parseTimeMs(pausedAt);
  if (pausedMs == null) return null;
  const age = now.getTime() - pausedMs;
  if (age < TELEMETRY_RELEASE_GREEN_MS) return MAP_ICON_TONE.moving;
  return MAP_ICON_TONE.intervalPause;
}

export function resolveTelemetryReleaseTone(releasedAt, now = new Date()) {
  const releasedMs = parseTimeMs(releasedAt);
  if (releasedMs == null) return null;
  const age = now.getTime() - releasedMs;
  if (age < TELEMETRY_RELEASE_GREEN_MS) return MAP_ICON_TONE.moving;
  if (age < TELEMETRY_RELEASE_YELLOW_MS) return MAP_ICON_TONE.pausedAssumed;
  return MAP_ICON_TONE.stoppedEmpty;
}

export function resolveMapIconTone({
  assumed = false,
  speed,
  lastMovedAt,
  assumedAt,
  reportedAt,
  telemetryReleasedAt,
  telemetryPausedAt,
  fallbackOffline = false,
  now = new Date(),
} = {}) {
  const pauseTone = resolveTelemetryPauseTone(telemetryPausedAt, now);
  if (pauseTone) return pauseTone;
  const releaseTone = resolveTelemetryReleaseTone(telemetryReleasedAt, now);
  if (releaseTone) return releaseTone;

  if (
    isMovingSpeed(speed) &&
    isFreshMapReport(reportedAt || lastMovedAt, now)
  ) {
    return MAP_ICON_TONE.moving;
  }

  const nowMs = now.getTime();
  const assumedMs = parseTimeMs(assumedAt);
  if (assumed && assumedMs != null && nowMs - assumedMs < STOPPED_TONE_MS) {
    return MAP_ICON_TONE.moving;
  }

  const movedMs = parseTimeMs(lastMovedAt) || assumedMs;
  if (movedMs == null) {
    if (assumed) return MAP_ICON_TONE.moving;
    return fallbackOffline
      ? MAP_ICON_TONE.stoppedEmpty
      : MAP_ICON_TONE.moving;
  }

  const stoppedLong = nowMs - movedMs >= STOPPED_TONE_MS;
  if (!stoppedLong) return MAP_ICON_TONE.moving;
  return assumed
    ? MAP_ICON_TONE.pausedAssumed
    : MAP_ICON_TONE.stoppedEmpty;
}
